/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalSearchModule} from '@ion/core/src/types/entities';
import AutocompleteSearch from '@ion/dls/src/components/search/AutocompleteSearch';
import useMedia from '@ion/dls/src/hooks/useMedia';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  isIdentifierLimitExceed,
  MAX_IDENTIFIER_SEARCH_LIMIT
} from '@ion/features/src/search/utils';
import {getSearchTitle} from './GlobalSearchTitle';
import {getSearchText} from './GlobalSearchUtils';
import {setGlobalSearch, toggleGlobalSearch} from './state';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    root : {
      width          : '510px',
      height         : '40px',
      display        : 'flex',
      flexDirection  : 'row',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    globalRoot : {
      width          : '350px',
      height         : '40px',
      display        : 'flex',
      flexDirection  : 'row',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    search : {
      flexGrow       : 1,
      alignItems     : 'center',
      border         : `1px solid ${dlsColor('grey5')}`,
      display        : 'flex',
      justifyContent : 'center'
    },
    errorMessage : {
      ...dlsTypography('body3'),
      color       : dlsColor('caRed'),
      fontSize    : '12px !important',
      paddingLeft : '16px !important'
    }
  };
});

export interface GlobalSearchWidgetProps {
  from?: string;
  isGlobalSearchMaxLength?: boolean;
  isCtcAdminGlobalSearch?: boolean;
}

/**
 * It will display GlobalSearchWidget, here user can enter keyword
 * to search data in Accounts/Users/Assets/Devices/Groups
 */
export const GlobalSearchWidget = ({
  from = '',
  isGlobalSearchMaxLength = false,
  isCtcAdminGlobalSearch = false
}: GlobalSearchWidgetProps) => {
  const [globalSearchOpen, setGlobalSearchOpen] = useState<boolean>(false);
  const [isValid, setIsValid]                   = useState<boolean>(true);
  const [options, setOptions]                   = useState([]);
  const {isPhone}                               = useMedia();
  const dispatch                                = useDispatch();
  const classes                                 = useStyles();
  const searchValue                             = useRef('');
  const fromHeader                              = from?.toLowerCase() === 'header';

  const {
    searchKeyword,
    searchModule,
    search : {
      isViewing: openGlobalSearch
    }
  } = useSelector(({globalSearch}) => globalSearch);

  const createSearchOptions = (val) => {
    const searchOptions = [];
    val = removeModule(val);
    const searchModules = Object.values(GlobalSearchModule);
    searchModules.forEach((module) => {
      const title = getSearchTitle(val);
      const optionsObj = {
        item : module,
        text : val,
        name : `${title} in ${module}`
      };
      searchOptions.push(optionsObj);
    });
    return searchOptions;
  };

  const removeModule = (str) => {
    const ind = str?.lastIndexOf(' in ');
    if (ind > -1) {
      return str?.substr(0, ind).trim();
    }
    return str;
  };

  function handleSearchChange(e: ChangeEvent<{}>, value: string) {
    setOptions([]);
    value = value.trim();
    setIsValid(!isIdentifierLimitExceed(value));
    if (value?.length) {
      const searchText = getSearchText(value);
      if (fromHeader) {
        setOptions(createSearchOptions(searchText));
      } else {
        setTimeout(() => {
          dispatch(setGlobalSearch(searchText, searchModule) as any);
        }, 1000);
      }
    } else {
      setTimeout(() => {
        dispatch(setGlobalSearch(value, searchModule) as any);
      }, 1000);
    }
  }

  function onSearchFocus() {
    setGlobalSearchOpen(true);
  }

  function onSearchBlur() {
    setGlobalSearchOpen(false);
  }

  function onResultSelect(
    e: ChangeEvent<{}>,
    value: unknown | unknown[]
  ) {
    let module = '';
    let keyword = '';
    if (value !== null) {
      module  = value?.item;
      keyword = value?.text;
      if (fromHeader) {
        dispatch(toggleGlobalSearch(true));
      }
    }
    if (!keyword?.endsWith('...')) {
      searchValue.current = keyword;
    } else {
      keyword = searchValue.current;
    }
    dispatch(setGlobalSearch(keyword, module) as any);
  }

  return (
    <div className={fromHeader ? classes.root : classes.globalRoot}>
      <div className={classes.search}>
        <AutocompleteSearch overrideTitleStyles
          options={options}
          onChange={handleSearchChange}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          onResultSelect={onResultSelect}
          recentSearchesTitle={''}
          recentSearches={[]}
          loading={true}
          inputLength={1}
          debounceDelay={100}
          isGlobalSearchMaxLength={isGlobalSearchMaxLength}
          isCtcAdminGlobalSearch={isCtcAdminGlobalSearch}
          shortPlaceholder={fromHeader ? globalSearchOpen : true} />
      </div>
      <Render.If condition={!isValid}>
        <div className={classes.errorMessage}>
          {translate('common:search.globalSearchLimitError', {limit: MAX_IDENTIFIER_SEARCH_LIMIT})}
        </div>
      </Render.If>
    </div>
  );
};

export default GlobalSearchWidget;
